import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });
const input = "bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";
const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export const AdminHolidays = ({ onAuthFail }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: "", name: "" });
  const [busy, setBusy] = useState(false);

  const fail = (err, msg) => {
    if (err?.response?.status === 401) onAuthFail?.();
    else toast.error(err?.response?.data?.detail || msg);
  };

  useEffect(() => {
    api.get("/holidays").then(({ data }) => setItems(data)).catch((e) => fail(e, "Could not load holidays"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.date || form.name.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.post("/holidays", { date: form.date, name: form.name.trim() });
      setItems(data); setForm({ date: "", name: "" }); toast.success("Holiday added");
    } catch (err) { fail(err, "Could not add holiday"); } finally { setBusy(false); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Remove "${h.name}" (${fmt(h.date)}) from the holiday list?`)) return;
    try {
      const { data } = await api.delete(`/holidays/${h.date}`);
      setItems(data); toast.success("Holiday removed");
    } catch (err) { fail(err, "Could not remove holiday"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="flex flex-col gap-6" data-testid="admin-holidays">
      <div>
        <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2"><CalendarDays size={13} className="text-nx-goldlight" /> Support bot holiday calendar</p>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">Sundays are always treated as holidays. Enquiries received on these dates get the holiday reply and are sent to the CEO at 9:30 AM on the next working day (reminders every 30 minutes until routed). Seeded with the Kerala Government calendar — add or remove dates any time.</p>
      </div>
      <form onSubmit={add} className="flex flex-wrap gap-2 items-end" data-testid="holiday-add-form">
        <label className="block w-full sm:w-auto"><span className="text-[10px] tracking-[0.18em] uppercase text-slate-500">Date</span>
          <input required type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={`${input} block mt-1 w-full sm:w-auto`} data-testid="holiday-date" /></label>
        <label className="block flex-1 min-w-[220px]"><span className="text-[10px] tracking-[0.18em] uppercase text-slate-500">Holiday name</span>
          <input required maxLength={120} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Thiruvonam" className={`${input} block mt-1 w-full`} data-testid="holiday-name" /></label>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3 disabled:opacity-50" data-testid="holiday-add-btn"><Plus size={14} /> Add Holiday</button>
      </form>
      <div className="border border-white/10 divide-y divide-white/5" data-testid="holiday-list">
        {items.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">No holidays listed.</p>}
        {items.map((h) => (
          <div key={h.date} className={`flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm ${h.date < today ? "opacity-50" : ""}`} data-testid={`holiday-row-${h.date}`}>
            <span className="text-nx-goldlight font-mono text-xs w-36 sm:w-44 shrink-0">{fmt(h.date)}</span>
            <span className="text-white flex-1 min-w-[160px] break-words">{h.name}</span>
            <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500 hidden sm:inline">{h.source === "manual" ? "added by CEO" : "Kerala Govt"}</span>
            <button onClick={() => remove(h)} className="text-slate-500 hover:text-red-400 p-1" title="Remove" data-testid={`holiday-remove-${h.date}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminHolidays;
